import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Buffer } from "buffer";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// If below line is removed failed to show pdf
// eslint-disable-next-line

import api from "app/api";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const AuthorizedPDF = ({ path, scale, showPages, width = "600px", size = "" }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [url, setUrl] = useState(null);
  const [loadProgress, setLoadProgress] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [downloadMB, setDownloadMB] = useState(null);
  const [documentKey, setDocumentKey] = useState("");
  const [loadError, setLoadError] = useState(false);


  useEffect(() => {
    if (!path) return;
    let isCancelled = false;

    const fetch = async () => {
      setLoadError(false);
      setLoadProgress(0);
      setLoadingPdf(true);
      setUrl(null);
      setDocumentKey("");
      setNumPages(null);
      setPageNumber(1);
      setDownloadMB(null);
      try {
        const response = await api.get(`/serviceapi/writeImageToClient?location=${path}`, {
          responseType: "arraybuffer",
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            const mb = (progressEvent.loaded / 1024 / 1024).toFixed(2);
            if (!isCancelled) {
              setLoadProgress(percentCompleted);
              setDownloadMB(mb);
            }
          },
        });

        if (isCancelled) return;

        if (response.data.byteLength === 0) {
          setDownloadMB("0.00");
          return;
        }  

        const base64Data = Buffer.from(response.data, "binary").toString("base64");
        const pdfDataUrl = `data:application/pdf;base64,${base64Data}`;
        setUrl(pdfDataUrl);
        setDocumentKey(`${path}-${Date.now()}`);
        setLoadProgress(null);
        setLoadingPdf(false);
      } catch (error) {
        if (!isCancelled) {
          console.error("PDF fetch failed:", error);
          setLoadError(true);
        }
      } finally {
        if (!isCancelled) {
          setLoadingPdf(false);
          setLoadProgress(null);
        }
      }
    };
    fetch();
    return () => {
      isCancelled = true;
    };
  }, [path, size]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onPageLoadStart = () => {
    setPageLoading(true);
  };

  const onPageLoadSuccess = () => {
    setPageLoading(false);
  };

  const nextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const LoadingComponent = () => (
    <div style={{ padding: "20px", fontSize: "18px", color: "#555" }}>
      <Stack sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 3, mb: 2, position: "relative", }}>
        <CircularProgress variant="determinate" value={loadProgress || 0} color="secondary" size={30} disableShrink />
        {loadProgress !== null && (
          <Typography variant="body2" color="textSecondary" sx={{ position: "absolute", mt: 1 }}>
            {loadProgress} % ({downloadMB} MB)
          </Typography>
        )}
        <Typography mt={6}>Fetching PDF...</Typography>
      </Stack>
    </div>
  );

  const PageLoadingComponent = () => (
    <Stack sx={{ justifyContent: "center", alignItems: "center", mt: 2, mb: 2 }}>
      <CircularProgress color="secondary" size={24} />
    </Stack>
  );

  return (
    <Stack>
      {(loadError) && (
        <Typography  mt={2}>
            Failed to load the PDF file. Please contact support.
        </Typography>
      )}
      {loadingPdf ? (
        <LoadingComponent />
      ) : (
        <Stack alignItems={"center"}>
          {showPages && numPages && (
            <Stack flexDirection={"row"} alignContent={"center"} justifyContent={"center"} alignItems={"center"}>
              <IconButton onClick={previousPage} disabled={pageNumber === 1}>
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography>
                Page {pageNumber} of {numPages}
              </Typography>
              <IconButton onClick={nextPage} disabled={pageNumber === numPages}>
                <KeyboardArrowRightIcon />
              </IconButton>
            </Stack>
          )}
          {url && !loadingPdf && !loadError && (
          <div style={{ width: width }}>
            <Document
              key={documentKey}
              file={{ url }}
              onLoadSuccess={onDocumentLoadSuccess}
              height="100%"
              // loading="Loading PDF..."
              loading={<PageLoadingComponent />}
              // options={{ workerSrc: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js` }}
            >
              {numPages && (
                <Page
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  customTextRenderer={false}
                  key={`page_${pageNumber}`}
                  className="pdf-page"
                  pageNumber={pageNumber}
                  scale={scale || "auto"}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={() => setPageLoading(false)}
                  onLoadProgress={onPageLoadStart}
                />
              )}
            </Document>
            {pageLoading && <PageLoadingComponent />}
          </div>
          )}
          {showPages && numPages && (
            <Stack flexDirection={"row"} justifyContent={"center"} alignItems={"center"} mt={1}>
              <IconButton onClick={previousPage} disabled={pageNumber === 1}>
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography>
                Page {pageNumber} of {numPages}
              </Typography>
              <IconButton onClick={nextPage} disabled={pageNumber === numPages}>
                <KeyboardArrowRightIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default AuthorizedPDF;
